import { Component, Inject, OnDestroy, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { AnnualPlanService } from '../../providers/annual-plans.services';
import { UsersProvider } from '../../../users/providers/users-provider';
import moment from 'moment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  MAT_DIALOG_DATA,
  MatDialogActions, MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Organization } from "../../../common/models/organization";
import { AnnualPlan } from "../../../common/models/annual-plan";
import { User } from "../../../common/models/user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { MatError, MatFormField, MatLabel } from "@angular/material/form-field";
import { DurationControlComponent } from "../../../common/components/duration-control/duration-control";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatInput } from "@angular/material/input";
import { MatButton } from "@angular/material/button";
import { NgIf, NgFor } from "@angular/common";
import { LangsProvider, Lang } from "../../../common/providers/langs-provider";
import { CdkTextareaAutosize } from "@angular/cdk/text-field";

@Component({
  selector: 'app-annual-plan-form',
  templateUrl: './annual-plan-form.component.html',
  styleUrls: ['./annual-plan-form.scss'],
  imports: [
    MatDialogTitle,
    TranslateModule,
    MatDialogContent,
    MatFormField,
    DurationControlComponent,
    MatDialogActions,
    MatInput,
    ReactiveFormsModule,
    MatButton,
    MatLabel,
    MatError,
    MatDialogClose,
    NgIf,
    FormsModule,
    NgFor,
    CdkTextareaAutosize,
    MatCheckboxModule
  ],
  standalone: true
})
export class AnnualPlanFormComponent implements OnInit, AfterViewInit, OnDestroy {

    public organization: Organization = {} as Organization;
    public annualPlan: AnnualPlan = {} as AnnualPlan;
    public currentUser: User = {} as User;
    public annualPlanForm: FormGroup;
    public isSubmitting = false;
    public minDate: any;
    public maxDate: any;
    public startDate = new Date();
    public selectedLanguages: number[] = [];
    public availableLangs: Lang[] = [];

    public languagesFormControl: FormControl = new FormControl<number[]>([], [
        (ctrl) => ctrl.value && ctrl.value.length > 0 ? null : { required: true }
      ]);

    public duration = 0;
    private subscriptions: Subscription[] = [];

    constructor(
        public dialog: MatDialogRef<AnnualPlanFormComponent>,
        private fb: FormBuilder,
        private annualPlanService: AnnualPlanService,
        private toaster: MatSnackBar,
        private utils: UtilsProvider,
        private langsProvider: LangsProvider,
        private cdr: ChangeDetectorRef,
        private translater: TranslateService,
        @Inject(MAT_DIALOG_DATA) public formData: any
    ) {
        this.annualPlanForm = this.fb.group({
            title: new FormControl('', [Validators.required]),
            weeksLength: new FormControl(52, [Validators.required, Validators.min(52), Validators.max(52)]),
            weeklyWorkingTime: new FormControl(0, [Validators.required, Validators.min(1), Validators.max(604800)]),
            comment: new FormControl('', [Validators.maxLength(1024)]),
        });
    }

    public ngAfterViewInit() {
        const langId = this.langsProvider.getBestLangId();
        
        if (this.formData.annualPlanRow) {
            this.formData.skipLangSelection = this.formData.skipLangSelection || false;
            this.selectedLanguages = !this.formData.skipLangSelection && this.formData.annualPlanRow.availableLangIds || [];
        }

        if (!this.selectedLanguages.includes(langId)) {
            this.selectedLanguages.push(langId);
        }

        this.languagesFormControl.setValue(this.selectedLanguages);

        this.availableLangs = this.langsProvider.getLangs();
        this.cdr.detectChanges();
    }

    public toggleLanguage(langId: number, checked: boolean): void {
        if (checked) {
            this.selectedLanguages.push(langId);
        } else {
            const index = this.selectedLanguages.indexOf(langId);
            this.selectedLanguages.splice(index, 1);
        }
    
        this.languagesFormControl.setValue(this.selectedLanguages);
    }

    public isLangSelected(langId: number): boolean {
        return this.selectedLanguages.includes(langId);
    }

    public ngOnInit() {
        if (this.formData.annualPlanRow) {
            this.annualPlanForm.patchValue(this.formData.annualPlanRow);
            this.duration = this.f['weeklyWorkingTime'].value;
        }
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    public submitForm() {
        this.isSubmitting = true;
        this.f['weeklyWorkingTime'].setValue(this.duration);

        if (this.annualPlanForm.valid && this.languagesFormControl.valid) {
            // update the model
            this.updateAnnualPlan(
                this.annualPlanForm.value,
                {id: this.formData.annualPlanRow ? this.formData.annualPlanRow.id : null},
                {library: this.formData.library},
                {organization: this.formData.organization}
            );

            // duplicate
            if (this.formData.toDuplicate || false) {
                this.subscriptions.push(
                    this.annualPlanService.duplicate(this.annualPlan, this.selectedLanguages).subscribe(
                        (data) => {
                            this.dialog.close(data);
                        },
                        (err) => {
                            this.isSubmitting = false;
                            if (err.error) {
                                this.toaster.open(err.error.message, '', {duration: 5000});
                            } else {
                                this.toaster.open(this.translater.instant('Error_unknown'), '', {duration: 5000});
                            }
                        }
                    )
                );
            } else {
                this.subscriptions.push(
                    this.annualPlanService.save(this.annualPlan, this.selectedLanguages).subscribe(
                        (data) => {
                            this.dialog.close(data);
                        },
                        (err) => {
                            this.isSubmitting = false;
                            if (err.error) {
                                this.toaster.open(err.error.message, '', {duration: 5000});
                            } else {
                                this.toaster.open(this.translater.instant('Error_unknown'), '', {duration: 5000});
                            }
                        }
                    )
                );
            }
        } else {
            this.utils.validateAllFormFields(this.annualPlanForm);
            this.isSubmitting = false;
        }
    }

    private updateAnnualPlan(...values: Object[]) {
        Object.assign(this.annualPlan, ...values);
    }

    get f() {
        return this.annualPlanForm.controls;
    }

    /**
     * Set endDate date picker min and max available dates
     */
    private updateMinMaxPickerDate() {
        // annual plan must be at least 4 months length
        this.minDate = moment(this.annualPlanForm.get('startedAt')?.value).add(4, 'months');

        // annual plan can not be longer than 1 year
        this.maxDate = moment(this.annualPlanForm.get('startedAt')?.value).add(1, 'years').subtract(1, 'day');
    }
}
